import { useCallback, useContext, useEffect } from "react";

import { useAnalytics, useSessionJWT } from "utils";
import { isSignedTOS, useTOSStatus } from "api/tos";
import { TOSContext } from "./TOSProvider";

// Local rather than `noop` from `utils`: NOT_GATED is frozen at module-eval time, and the import
// cycle between `utils` and this module graph means a `utils` export is not reliably initialised
// that early.
const NOOP = () => {};

// What every un-opted-in call site sees. Frozen so a consumer cannot mutate the shared object.
const NOT_GATED = Object.freeze({
  required: false,
  agreed: false,
  setAgreed: NOOP,
  loading: false,
  error: null,
  signError: null,
  signing: false,
  blockedByTOS: false,
  signTOS: async () => {},
  ensureNotBlocked: async () => true,
  requestConsentAttention: NOOP,
});

// Stands in for the error a disabled status query never reports: without a `sessionJWT` the query
// does not run, and reads `isSuccess: false` with `error: null` - so the gate would block every CTA
// with nothing rendered anywhere to say why. Hosts already surface `error`.
const NO_SESSION_ERROR = new Error("Unable to establish ToS status: no session");

/**
 * `TOS_JIT_Required` fires once per account, not once per mount: consent blocks unmount on every
 * Checkout <-> /addpm switch, so a per-instance ref would double-count the funnel metric.
 */
const firedRequired = new Set();

/**
 * For tests - module state outlives a render tree.
 */
export const resetTOSAnalytics = () => firedRequired.clear();

/**
 * useTOSGate
 * @param {object} [options]
 * @param {boolean} [options.enabled=false] - opt in to gating. A surface that passes nothing keeps
 * today's behavior and makes no network call.
 * @returns {{
 *   required: boolean|undefined,
 *   agreed: boolean,
 *   setAgreed: Function,
 *   loading: boolean,
 *   error: Error|null,
 *   signError: Error|null,
 *   signing: boolean,
 *   blockedByTOS: boolean,
 *   ensureNotBlocked: () => Promise<boolean>,
 *   signTOS: () => Promise<any>,
 *   requestConsentAttention: Function,
 * }} `required` is `undefined` until the status query resolves; `blockedByTOS` is the single value
 * hosts gate their CTA on, and folds in both "status unknown" and "consent needed but unticked".
 */
export const useTOSGate = ({ enabled = false } = {}) => {
  const tos = useContext(TOSContext);
  const analytics = useAnalytics();
  const { sessionJWT, sessionData } = useSessionJWT();
  // account_id, not sessionData: useSessionJWT re-decodes the JWT into a fresh object every render,
  // so the object identity would re-run the effect below on every render.
  const accountId = sessionData?.account_id;

  const { data, isLoading, isSuccess, error, refetch } = useTOSStatus({ enabled });
  // `undefined` unless the status query resolved successfully: loading, retrying, errored and
  // query-disabled all read as unknown, so unknown fails closed below.
  const required = isSuccess ? !isSignedTOS(data) : undefined;
  const statusError = error ?? (enabled && !sessionJWT ? NO_SESSION_ERROR : null);
  const blockedByTOS = !isSuccess || Boolean(required && !tos?.agreed);

  const registerGate = tos?.registerGate;
  const requestConsentAttention = tos?.requestConsentAttention;

  // Above the early returns, guarded internally on `enabled`. Below them it would be a conditional
  // hook call.
  useEffect(() => {
    if (!enabled || required !== true || !accountId || firedRequired.has(accountId)) return;
    firedRequired.add(accountId);
    analytics?.track?.("TOS_JIT_Required");
  }, [enabled, required, accountId, analytics]);

  // What tells the provider a gated screen is on-screen, so it knows when the last one leaves and
  // the tick should be dropped. See registerGate in TOSProvider.
  useEffect(() => {
    if (!enabled || !registerGate) return;
    return registerGate();
  }, [enabled, registerGate]);

  /**
   * ensureNotBlocked
   * @returns {Promise<boolean>} true when the caller may go on to sign and act.
   * @description The other half of `blockedByTOS`, for CTAs that cannot express it as an
   * `isDisabled` - a Box or a Select with a raw onClick. The two reasons a click is blocked need
   * opposite responses: an unticked block can be scrolled to, but an unresolved status has nothing
   * to scroll to - a closed `<Collapse>` is `display: none`, and `scrollIntoView` on a hidden
   * subtree is a no-op - so that case waits the status out and re-decides instead.
   */
  const ensureNotBlocked = useCallback(async () => {
    if (!blockedByTOS) return true;
    if (required === true) {
      requestConsentAttention?.();
      return false;
    }

    // cancelRefetch false so this rides the fetch already in flight rather than starting a second
    // GET - the common case here is a click landing mid-fetch
    const result = await refetch({ cancelRefetch: false });
    // Still unknown. The failure reads through `error`, so the click is accounted for even though
    // nothing happens.
    if (!result?.isSuccess) return false;
    if (isSignedTOS(result.data)) return true;

    // Consent is needed after all. Deferred a tick because `required` has only just flipped: the
    // <Collapse> is still display:none until React commits, and scrolling into a hidden subtree
    // does nothing.
    setTimeout(() => requestConsentAttention?.(), 0);
    return false;
  }, [blockedByTOS, required, refetch, requestConsentAttention]);

  if (!enabled) return NOT_GATED;
  if (!tos) throw new Error("useTOSGate({ enabled: true }) requires a <TOSProvider> ancestor");

  return {
    required,
    agreed: tos.agreed,
    setAgreed: tos.setAgreed,
    loading: isLoading,
    error: statusError,
    // From the provider's single mutation, not a local one
    signError: tos.signError,
    signing: tos.signing,
    blockedByTOS,
    ensureNotBlocked,
    // Unconditional on purpose - it must not close over `required`. `if (required) await sign()`
    // resolves WITHOUT signing whenever the status is unknown, and double-POSTs when a sibling has
    // already signed but this render has not observed it. The decision belongs to useSignTOS's
    // cache read.
    signTOS: tos.signTOS,
    requestConsentAttention: tos.requestConsentAttention,
  };
};

export default useTOSGate;
